import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, API, formatApiError, formatCurrency, formatDateTime } from "@/lib/api";
import { log } from "@/lib/log";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/StatusBadge";
import { ReceiptViewer } from "@/components/ReceiptViewer";
import { CheckCircle2, XCircle, Loader2, Lock, FileText, ExternalLink, ShieldCheck } from "lucide-react";
import axios from "axios";

// Public axios instance — must NOT send Authorization headers
const publicApi = axios.create({ baseURL: API });

export default function PublicApproval() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [comment, setComment] = useState("");
  const [acting, setActing] = useState(null);
  const [done, setDone] = useState(null); // 'approved' | 'rejected' | null

  const load = useCallback(async () => {
    try {
      const { data } = await publicApi.get(`/approvals/${token}`);
      setData(data);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const decide = async (decision) => {
    setActing(decision);
    setError("");
    try {
      const { data: updated } = await publicApi.post(`/approvals/${token}/decision`, {
        decision, comment: comment.trim() || null,
      });
      setData(updated);
      setDone(decision);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setActing(null);
    }
  };

  if (loading) {
    return (
      <ApprovalShell>
        <div className="py-12 flex items-center justify-center text-ink-soft">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading expense…
        </div>
      </ApprovalShell>
    );
  }
  if (error && !data) {
    return (
      <ApprovalShell>
        <ErrorPanel title="This approval link can't be opened" message={error} />
      </ApprovalShell>
    );
  }
  if (!data) return null;

  const finalized = data.status === "approved" || data.status === "rejected";
  const used = data.approver_consumed || data.approver_decision !== "pending";
  const receipts = data.receipts && data.receipts.length
    ? data.receipts
    : (data.receipt_filename ? [{ filename: data.receipt_filename, content_type: data.receipt_content_type }] : []);

  return (
    <ApprovalShell>
      <div className="grid lg:grid-cols-12 gap-6 animate-fade-up">
        {/* Receipts */}
        <div className="lg:col-span-5">
          <div className="bg-white border border-cream-200 rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-cream-200 flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-[0.18em] text-ink-soft font-semibold">
                {receipts.length > 1 ? `${receipts.length} receipts` : "Receipt"}
              </div>
            </div>
            <div data-testid="public-receipts-list">
              {receipts.length === 0 ? (
                <div className="p-6 text-center text-ink-soft text-sm">No receipts attached.</div>
              ) : (
                <ReceiptViewer
                  receipts={receipts}
                  testidPrefix="public-receipt"
                  fetchBlob={async (idx) => {
                    const res = await publicApi.get(`/approvals/${token}/receipt/${idx}`, { responseType: "blob" });
                    return res.data;
                  }}
                />
              )}
            </div>
          </div>
        </div>

        {/* Decision */}
        <div className="lg:col-span-7 space-y-6">
          <section className="bg-white border border-cream-200 rounded-xl p-6 md:p-8">
            <div className="flex items-baseline justify-between gap-3 flex-wrap mb-1">
              <div className="text-[11px] uppercase tracking-[0.18em] text-ink-soft">Expense for review</div>
              <StatusBadge status={data.status} />
            </div>
            <h1 className="font-heading text-3xl text-forest-900 tracking-tight" data-testid="public-payee">{data.payee}</h1>
            <div className="font-heading text-3xl text-forest-800 mt-1" data-testid="public-amount">
              {formatCurrency(data.amount, data.currency)}
            </div>
            <p className="text-sm text-ink-soft mt-4 leading-relaxed">{data.description}</p>

            <dl className="mt-6 grid grid-cols-2 gap-x-6 gap-y-3 text-sm border-t border-cream-200 pt-5">
              <KV k="Submitted by" v={data.submitter} />
              <KV k="Submitted" v={formatDateTime(data.created_at)} />
              <KV k="Reviewing as" v={<span data-testid="public-approver-email">{data.approver_email}</span>} />
              <KV k="So far" v={`${data.decisions_summary.approved}/3 approved${data.decisions_summary.rejected ? ` · ${data.decisions_summary.rejected} rejected` : ""}`} />
            </dl>

            {/* Decision panel */}
            {used || finalized ? (
              <DecisionDone data={data} done={done} />
            ) : (
              <div className="mt-6 pt-6 border-t border-cream-200">
                <label className="text-xs font-semibold uppercase tracking-wider text-ink-soft" htmlFor="approver-comment">
                  Comment (optional)
                </label>
                <Textarea
                  id="approver-comment"
                  rows={3}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  data-testid="public-comment-input"
                  placeholder="Add a note for the audit log…"
                  className="mt-1.5 bg-white border-cream-200 focus-visible:ring-forest-100 focus-visible:border-forest-700"
                />
                {error && (
                  <div data-testid="public-decision-error" className="mt-4 text-sm text-rose-800 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">
                    {error}
                  </div>
                )}
                <div className="mt-5 flex items-center gap-3">
                  <Button
                    onClick={() => decide("approved")}
                    disabled={!!acting}
                    data-testid="public-approve-button"
                    className="bg-forest-700 hover:bg-forest-800 text-cream-50 h-11 px-6"
                  >
                    {acting === "approved" ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><CheckCircle2 className="h-4 w-4 mr-1.5" /> Approve</>)}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => decide("rejected")}
                    disabled={!!acting}
                    data-testid="public-reject-button"
                    className="border-rose-200 text-rose-800 hover:bg-rose-50 h-11 px-6"
                  >
                    {acting === "rejected" ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><XCircle className="h-4 w-4 mr-1.5" /> Reject</>)}
                  </Button>
                </div>
              </div>
            )}
          </section>

          <div className="text-xs text-ink-mute flex items-center gap-2 px-1">
            <Lock className="h-3 w-3" /> This link is unique to {data.approver_email} and can only be used once. Every action is written to a tamper-proof audit log.
          </div>
        </div>
      </div>
    </ApprovalShell>
  );
}

function ApprovalShell({ children }) {
  return (
    <div className="min-h-screen bg-cream-50">
      <header className="border-b border-cream-200 bg-cream-50/90 backdrop-blur sticky top-0 z-30">
        <div className="mx-auto max-w-5xl px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="h-8 w-8 rounded-md bg-forest-700 grid place-items-center">
              <span className="font-heading text-cream-50 text-sm font-semibold">3</span>
            </div>
            <div className="leading-tight">
              <div className="font-heading text-[15px] font-semibold text-forest-800">Sign-Off</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-ink-soft">Approval review</div>
            </div>
          </div>
          <div className="hidden sm:flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-ink-mute">
            <ShieldCheck className="h-3.5 w-3.5" /> Secure single-use link
          </div>
        </div>
      </header>
      <main className="mx-auto max-w-5xl px-6 md:px-10 py-8 md:py-12">{children}</main>
    </div>
  );
}

function KV({ k, v }) {
  return (
    <div>
      <dt className="text-[11px] uppercase tracking-wider text-ink-mute">{k}</dt>
      <dd className="text-sm text-ink mt-0.5 break-all">{v}</dd>
    </div>
  );
}

function ErrorPanel({ title, message }) {
  return (
    <div className="max-w-md mx-auto text-center py-12" data-testid="public-error-panel">
      <div className="mx-auto h-12 w-12 rounded-full bg-rose-50 grid place-items-center mb-4">
        <XCircle className="h-5 w-5 text-rose-700" />
      </div>
      <h2 className="font-heading text-xl text-forest-900 mb-2">{title}</h2>
      <p className="text-sm text-ink-soft">{message}</p>
    </div>
  );
}

function DecisionDone({ data, done }) {
  const isApproved = (done || data.approver_decision) === "approved";
  return (
    <div
      data-testid="public-decision-done"
      className={`mt-6 pt-6 border-t border-cream-200`}
    >
      <div className={`rounded-lg p-5 border ${isApproved ? "bg-emerald-50 border-emerald-200" : "bg-rose-50 border-rose-200"}`}>
        <div className="flex items-center gap-2">
          {isApproved
            ? <CheckCircle2 className="h-5 w-5 text-emerald-700" />
            : <XCircle className="h-5 w-5 text-rose-700" />}
          <div className={`font-heading text-lg ${isApproved ? "text-emerald-900" : "text-rose-900"}`}>
            {isApproved ? "Thank you — your approval is recorded" : "Recorded — you rejected this expense"}
          </div>
        </div>
        <p className={`text-sm mt-2 ${isApproved ? "text-emerald-800" : "text-rose-800"}`}>
          This link is now used and cannot be reopened. The submitter has been notified of the updated status.
        </p>
      </div>
    </div>
  );
}
